import { createContext, useContext, useEffect, useState } from "react";

export const CATEGORIES = ["GST Updates", "Income Tax", "Compliance Deadlines", "Business Tips"];

export const translations = {
  en: {
    nav: { services: "Services", about: "Approach", journal: "Journal", book: "Book a consultation" },
    hero: {
      eyebrow: "GST clarity for growing businesses",
      l1: "Good advice.",
      l2: "Better business.",
      sub: "GST shouldn’t slow your business down. We make compliance simple, practical and dependable — so you can get on with what matters.",
      cta1: "Book a consultation",
      cta2: "Explore our services",
      trust1: "Trusted guidance",
      trust2: "Prompt support",
      stamp: "15+",
      stampSmall: "Years of experience",
      imgAlt: "Consultants discussing a business’s GST strategy",
      floats: [
        { t: "GSTR-3B", s: "Filed on time" },
        { t: "Input Tax Credit", s: "Reconciled · GSTR-2B" },
        { t: "Next deadline", s: "20 July" },
      ],
    },
    marquee: ["GSTR-1", "GSTR-3B", "Input Tax Credit", "E-Invoicing", "Annual Return", "GST Registration", "E-way Bill", "Refund Claims", "Composition Scheme", "Place of Supply"],
    statement: {
      eyebrow: "WHY RKJ",
      t1: "Tax compliance is more than a checklist.",
      t2: "It’s peace of mind.",
      body: "We’re a GST consultancy built for entrepreneurs, growing businesses and established teams who value straight answers, thoughtful advice and a partner who picks up the phone.",
    },
    servicesSec: {
      eyebrow: "WHAT WE DO",
      t1: "Expertise that moves",
      t2: "with your business.",
      sub: "From registration to resolution, we bring clarity to every stage of your GST journey.",
      enquire: "Enquire",
    },
    services: [
      { id: "gst-registration", t: "GST Registration", d: "Get your business registered right the first time, with documentation and compliance guidance from day one." },
      { id: "return-filing", t: "Return Filing", d: "Accurate, on-time GSTR-1, GSTR-3B and annual returns so you can focus on running your business." },
      { id: "gst-audit", t: "GST Audit & Review", d: "A practical review of your books, ITC and processes to find risks before they become notices." },
      { id: "gst-notices", t: "Notices & Litigation", d: "Clear representation and thoughtful replies for GST notices, assessments and departmental proceedings." },
      { id: "eway-bill", t: "E-way Bill Support", d: "Avoid disruptions with quick help for e-way bills, e-invoicing and movement of goods compliance." },
      { id: "gst-advisory", t: "Advisory & Planning", d: "Make better tax decisions with accessible advice on transactions, structures and changing GST rules." },
    ],
    about: {
      eyebrow: "THE RKJ APPROACH",
      t1: "We speak your",
      t2: "language.",
      body: "Tax can feel complicated. Our job is to make it feel clear. We translate the fine print into practical next steps, without jargon or unnecessary layers.",
      checks: ["Advice that’s easy to act on", "A proactive compliance rhythm", "One reliable point of contact"],
      cta: "Meet your GST partner",
      cap1: "Clear conversations.",
      cap2: "Confident decisions.",
      imgAlt: "The RKJ team in a client consultation",
    },
    tools: {
      eyebrow: "TOOLS FOR YOU",
      t1: "Small tools.",
      t2: "Useful clarity.",
      body: "We believe good advice should be accessible every day — not only when a deadline is around the corner.",
      p1t: "GST knowledge base",
      p1d: "Plain-language answers to common questions.",
      p2t: "Practical calculations",
      p2d: "Start with an estimate, then let’s talk specifics.",
    },
    calc: { eyebrow: "QUICK ESTIMATE", title: "GST calculator", body: "Get a quick estimate of the GST on your transaction. For guidance on your exact liability, talk to us.", taxable: "Taxable value", rate: "GST rate", result: "Estimated GST" },
    insights: { eyebrow: "FROM THE DESK", t1: "GST, made", t2: "understandable.", read: "Read article", viewAll: "View all articles" },
    writer: {
      title: "Article title", excerpt: "Short summary", content: "Full article — supports headings (##), **bold** and lists",
      tags: "Tags, comma separated", category: "Category", titleHi: "Hindi title (optional)", excerptHi: "Hindi summary (optional)", contentHi: "Hindi article (optional)",
      publish: "Publish article", cancel: "Cancel", success: "Your article is now live.", error: "Could not publish the article.",
    },
    quote: { text: "RKJ gave us the confidence to stop worrying about compliance and start planning for growth.", author: "— Founder, growing retail business" },
    banner: { eyebrow: "READY WHEN YOU ARE", t1: "Let’s make GST", t2: "feel simpler.", cta: "Start a conversation" },
    footer: { tagline: "Thoughtful GST advice for businesses that are going places.", explore: "Explore", rights: "© 2026 RKJ GST Consultancy. All rights reserved." },
    modal: {
      eyebrow: "LET’S TALK", title: "Bring us your GST questions.", sub: "Tell us a little about what you need. We’ll get back to you within one business day.",
      name: "Your name", email: "Work email", phone: "Phone number", message: "How can we help?", submit: "Send enquiry",
      success: "Thank you — we’ll be in touch shortly.", error: "Please try again in a moment.",
    },
    blog: {
      eyebrow: "INSIGHTS & ANALYSIS", title: "The GST Journal", sub: "Daily, plain-language reading on GST, income tax, compliance and running a business in India.",
      search: "Search articles…", all: "All", write: "Write an article", empty: "No articles match your search.", min: "min read", by: "By",
      imgAlt: "Tax documents and ledger on a desk",
    },
    article: { back: "All articles", share: "Share this article", copied: "Link copied to clipboard", related: "Keep reading", tags: "Filed under", min: "min read", notFound: "This article could not be found.", notFoundCta: "Back to the journal", loading: "Loading article…" },
    cats: { "GST Updates": "GST Updates", "Income Tax": "Income Tax", "Compliance Deadlines": "Compliance Deadlines", "Business Tips": "Business Tips" },
    wa: { greeting: "Hello RKJ GST Consultancy, I have a GST question." },
  },
  hi: {
    nav: { services: "सेवाएँ", about: "हमारी सोच", journal: "जर्नल", book: "परामर्श बुक करें" },
    hero: {
      eyebrow: "बढ़ते कारोबारों के लिए GST की स्पष्टता",
      l1: "सही सलाह.",
      l2: "बेहतर कारोबार.",
      sub: "GST आपके कारोबार की रफ़्तार नहीं घटानी चाहिए। हम अनुपालन को सरल, व्यावहारिक और भरोसेमंद बनाते हैं — ताकि आप ज़रूरी काम पर ध्यान दे सकें।",
      cta1: "परामर्श बुक करें",
      cta2: "हमारी सेवाएँ देखें",
      trust1: "भरोसेमंद मार्गदर्शन",
      trust2: "त्वरित सहायता",
      stamp: "15+",
      stampSmall: "वर्षों का अनुभव",
      imgAlt: "कारोबार की GST रणनीति पर चर्चा करते सलाहकार",
      floats: [
        { t: "GSTR-3B", s: "समय पर फ़ाइल" },
        { t: "इनपुट टैक्स क्रेडिट", s: "मिलान पूर्ण · GSTR-2B" },
        { t: "अगली तिथि", s: "20 जुलाई" },
      ],
    },
    marquee: ["जीएसटीआर-1", "जीएसटीआर-3बी", "इनपुट टैक्स क्रेडिट", "ई-इनवॉइसिंग", "वार्षिक रिटर्न", "जीएसटी पंजीकरण", "ई-वे बिल", "रिफंड दावे", "कम्पोज़िशन योजना", "प्लेस ऑफ़ सप्लाई"],
    statement: {
      eyebrow: "RKJ क्यों",
      t1: "कर अनुपालन सिर्फ़ एक चेकलिस्ट नहीं है।",
      t2: "यह मन की शांति है।",
      body: "हम एक GST परामर्श संस्था हैं — उद्यमियों, बढ़ते कारोबारों और स्थापित टीमों के लिए, जो सीधे जवाब, विचारशील सलाह और फ़ोन उठाने वाला साथी चाहते हैं।",
    },
    servicesSec: {
      eyebrow: "हम क्या करते हैं",
      t1: "विशेषज्ञता जो चलती है",
      t2: "आपके कारोबार के साथ।",
      sub: "पंजीकरण से समाधान तक — GST की हर अवस्था में स्पष्टता।",
      enquire: "पूछताछ करें",
    },
    services: [
      { id: "gst-registration", t: "GST पंजीकरण", d: "पहली बार में सही पंजीकरण — दस्तावेज़ और अनुपालन मार्गदर्शन के साथ, पहले दिन से।" },
      { id: "return-filing", t: "रिटर्न फ़ाइलिंग", d: "सटीक और समय पर GSTR-1, GSTR-3B और वार्षिक रिटर्न — ताकि आप कारोबार पर ध्यान दें।" },
      { id: "gst-audit", t: "GST ऑडिट व समीक्षा", d: "आपकी बही-खाता, ITC और प्रक्रियाओं की व्यावहारिक समीक्षा — नोटिस बनने से पहले जोखिम पहचानें।" },
      { id: "gst-notices", t: "नोटिस व विवाद", d: "GST नोटिस, आकलन और विभागीय कार्यवाही के लिए स्पष्ट प्रतिनिधित्व और सुविचारित उत्तर।" },
      { id: "eway-bill", t: "ई-वे बिल सहायता", d: "ई-वे बिल, ई-इनवॉइसिंग और माल की आवाजाही के अनुपालन में त्वरित मदद — बिना रुकावट।" },
      { id: "gst-advisory", t: "सलाह व योजना", d: "लेन-देन, संरचना और बदलते GST नियमों पर सरल सलाह — बेहतर कर निर्णयों के लिए।" },
    ],
    about: {
      eyebrow: "RKJ की सोच",
      t1: "हम बोलते हैं",
      t2: "आपकी भाषा।",
      body: "कर कभी-कभी जटिल लग सकता है। हमारा काम उसे स्पष्ट बनाना है। हम बारीक छाप को व्यावहारिक अगले कदमों में बदलते हैं — बिना शब्दजाल, बिना अनावश्यक परतों के।",
      checks: ["सलाह जिस पर तुरंत अमल हो सके", "सक्रिय अनुपालन की नियमित लय", "एक भरोसेमंद संपर्क सूत्र"],
      cta: "अपने GST साथी से मिलें",
      cap1: "स्पष्ट बातचीत।",
      cap2: "आत्मविश्वास भरे निर्णय।",
      imgAlt: "क्लाइंट परामर्श में RKJ टीम",
    },
    tools: {
      eyebrow: "आपके लिए उपकरण",
      t1: "छोटे उपकरण।",
      t2: "बड़ी स्पष्टता।",
      body: "हम मानते हैं अच्छी सलाह रोज़ मिलनी चाहिए — सिर्फ़ डेडलाइन के समय नहीं।",
      p1t: "GST ज्ञानकोश",
      p1d: "आम सवालों के सरल भाषा में उत्तर।",
      p2t: "व्यावहारिक गणनाएँ",
      p2d: "अनुमान से शुरुआत करें, फिर विस्तार से बात करें।",
    },
    calc: { eyebrow: "त्वरित अनुमान", title: "GST कैलकुलेटर", body: "अपने लेन-देन पर GST का तुरंत अनुमान लगाएँ। सटीक देनदारी के लिए हमसे बात करें।", taxable: "कर योग्य राशि", rate: "GST दर", result: "अनुमानित GST" },
    insights: { eyebrow: "हमारी डेस्क से", t1: "GST, अब", t2: "समझ में।", read: "लेख पढ़ें", viewAll: "सभी लेख देखें" },
    writer: {
      title: "लेख का शीर्षक", excerpt: "संक्षिप्त सार", content: "पूरा लेख — शीर्षक (##), **बोल्ड** और सूचियाँ समर्थित",
      tags: "टैग, अल्पविराम से अलग", category: "श्रेणी", titleHi: "हिंदी शीर्षक (वैकल्पिक)", excerptHi: "हिंदी सार (वैकल्पिक)", contentHi: "हिंदी लेख (वैकल्पिक)",
      publish: "लेख प्रकाशित करें", cancel: "रद्द करें", success: "आपका लेख प्रकाशित हो गया।", error: "लेख प्रकाशित नहीं हो सका।",
    },
    quote: { text: "RKJ की वजह से हम अनुपालन की चिंता छोड़कर विकास की योजना बनाने लगे।", author: "— संस्थापक, एक बढ़ता रिटेल कारोबार" },
    banner: { eyebrow: "जब आप तैयार हों", t1: "आइए GST को", t2: "सरल बनाएँ।", cta: "बातचीत शुरू करें" },
    footer: { tagline: "आगे बढ़ते कारोबारों के लिए विचारशील GST सलाह।", explore: "देखें", rights: "© 2026 RKJ GST Consultancy. सर्वाधिकार सुरक्षित।" },
    modal: {
      eyebrow: "बात करते हैं", title: "अपने GST सवाल हमें दीजिए।", sub: "हमें बताइए आपको क्या चाहिए। हम एक कार्यदिवस के भीतर उत्तर देंगे।",
      name: "आपका नाम", email: "ईमेल", phone: "फ़ोन नंबर", message: "हम कैसे मदद करें?", submit: "पूछताछ भेजें",
      success: "धन्यवाद — हम शीघ्र संपर्क करेंगे।", error: "कृपया थोड़ी देर बाद प्रयास करें।",
    },
    blog: {
      eyebrow: "विश्लेषण और जानकारी", title: "GST जर्नल", sub: "GST, आयकर, अनुपालन और भारत में कारोबार पर रोज़ाना सरल भाषा में लेख।",
      search: "लेख खोजें…", all: "सभी", write: "लेख लिखें", empty: "आपकी खोज से कोई लेख नहीं मिला।", min: "मिनट पठन", by: "लेखक",
      imgAlt: "डेस्क पर कर दस्तावेज़ और बही-खाता",
    },
    article: { back: "सभी लेख", share: "यह लेख साझा करें", copied: "लिंक कॉपी हो गया", related: "आगे पढ़ें", tags: "टैग", min: "मिनट पठन", notFound: "यह लेख नहीं मिला।", notFoundCta: "जर्नल पर लौटें", loading: "लेख खुल रहा है…" },
    cats: { "GST Updates": "GST अपडेट", "Income Tax": "आयकर", "Compliance Deadlines": "अनुपालन तिथियाँ", "Business Tips": "कारोबारी सुझाव" },
    wa: { greeting: "नमस्ते RKJ GST Consultancy, मुझे GST से जुड़ा एक सवाल है।" },
  },
};

const LangContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("rkj-lang") || "en");
  useEffect(() => {
    localStorage.setItem("rkj-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const t = (key) =>
    key.split(".").reduce((acc, k) => acc?.[k], translations[lang]) ??
    key.split(".").reduce((acc, k) => acc?.[k], translations.en) ??
    key;
  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
